import { Canceler } from 'axios';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { showProductModalErrorMessage, validateMultiChoiceModifier, validateSingleChoiceModifier } from '../helpers/orderProductModal';
import { modalStore } from 'src/mobx/modalStore';
import { requests } from 'src/requests';
import client from 'src/services/client';
import { IBranch, ICurrencySettings } from 'src/types/branches';
import { IMenuProduct, IResponseProduct, IResponseProductModifier, IResponseProductSize } from 'src/types/products';
import { getCancelToken, getCurrencySettings } from 'src/utils/helpers';
import { DELIVERY_TYPE } from '~/constants/deliveryTypes';
import { appStore } from '~/mobx/appStore';
import logger from '~/services/logger';
import { IResponseComboGroupWithItems, IResponseComboItem } from '~/types/combo';
import { productUtils } from '~/utils/productUtils';

export class OrderProductModalStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;

  @observable private loading: boolean = false;
  @observable public loadingProduct: boolean = false;

  @observable public product: IMenuProduct | undefined = undefined;
  @observable public branch: IBranch | undefined = undefined;
  @observable orderType: DELIVERY_TYPE | undefined = undefined;

  @observable public crossSellingLoading: boolean = false;
  @observable public crossSellingProducts: IMenuProduct[] = [];
  @observable public time?: number;

  /**
   * Currency setting for formatPrice
   * Depends on selected branch
   *
   * @see getCurrencySettings
   * @type {(ICurrencySettings | undefined)}
   */
  @computed public get currencySettings(): ICurrencySettings | undefined {
    return getCurrencySettings(this.branch);
  }

  @computed public get productError(): string | undefined {
    return modalStore.orderProduct?.error;
  }

  @computed public get mealComboError(): boolean {
    return productUtils.isInvalidMealCombo(this.product);
  }

  @computed public get currentSizeGroups(): IResponseComboGroupWithItems[] {
    return productUtils.getCurrentMealSizeGroups(this.product);
  }

  @observable public needScrollToError: boolean = false;
  @action public setNeedScrollToError = (need: boolean) => {
    this.needScrollToError = need;
  };

  @observable public modifiersWithError: string[] = [];
  @action public validateModifiers = () => {
    if (!this.product) return;

    const idx = this.product.modifiers.reduce((sum: string[], modifier) => {
      const valid: boolean = modifier.multi_choice ? validateMultiChoiceModifier(modifier) : validateSingleChoiceModifier(modifier);

      if (!valid) sum.push(String(modifier.id));

      return sum;
    }, []);

    this.product.items?.forEach((item) => {
      const isSelectedItem: boolean = Boolean(item.size_id === this.product?.size?.id && item.is_selected);

      if (isSelectedItem) {
        item.modifiers?.forEach((modifier) => {
          const valid: boolean = modifier.multi_choice ? validateMultiChoiceModifier(modifier) : validateSingleChoiceModifier(modifier);

          if (!valid) idx.push(`${productUtils.getMealItemKey(item)}-${modifier.id}`);
        });
      }
    });

    this.modifiersWithError = idx;
  };

  @action public init = () => {
    this.loading = false;

    this.product = toJS(modalStore.orderProduct?.product);
    this.branch = toJS(modalStore.orderProduct?.branch);
    this.orderType = toJS(modalStore.orderProduct?.orderType);
    this.time = modalStore.orderProduct?.time;

    this.crossSellingLoading = false;
    this.crossSellingProducts = [];

    this.needScrollToError = false;
    this.modifiersWithError = [];

    if (!this.product) {
      this.getProduct();
    } else {
      this.loadingProduct = true;
      // eslint-disable-next-line promise/catch-or-return
      requests
        .fetchProduct(this.product.id, this.branch?.id)
        .then((response) => {
          const [updatedProduct] = productUtils.transformProductsFromCalculator([this.product!], [response]);

          this.product = updatedProduct;
          return;
        })
        .catch((error) => {
          console.warn(error);
        })
        .finally(() => {
          this.loadingProduct = false;
        });
    }
    if (this.product?.upsales_items.length) this.fetchCrossSellingProducts();
  };

  @action private getProduct = async () => {
    if (!this.product?.id) return;

    try {
      this.loading = true;

      const product: IMenuProduct = await requests.fetchProduct(this.product.id, this.branch?.id);

      this.product = product;
      this.loading = false;

      if (this.product.upsales_items.length) this.fetchCrossSellingProducts();
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'OrderProductStore getProduct' });
    }
  };

  @action private fetchCrossSellingProducts = async () => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.crossSellingLoading = true;

      let products = await client.get(`/v3/products`, {
        params: {
          products: this.product?.upsales_items,
          branch: this?.branch?.id,
        },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      products = products.map((product: IResponseProduct) => productUtils.transformProduct(product));

      this.crossSellingProducts = products;
      this.crossSellingLoading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.crossSellingLoading = false;
      logger.error({ error, place: 'OrderProductStore fetchCrossSellingProducts' });
    }
  };

  @action public onChangeQuantity = (value: number) => {
    this.product = this.product ? { ...this.product, quantity: value } : undefined;
  };

  @action public onChangeSize = (size: IResponseProductSize) => {
    this.product = this.product ? { ...this.product, size } : undefined;
  };

  @action public onChangeModifier = (modifier: IResponseProductModifier) => {
    if (!this.product) return;

    const newModifiers = this.product.modifiers.map((_modifier) => (_modifier.id === modifier.id ? modifier : _modifier));

    this.product = { ...this.product, modifiers: newModifiers };

    if (this.modifiersWithError.length) this.validateModifiers();
  };
  @action public onChangeModifierInComboItem = (item: IResponseComboItem, modifier: IResponseProductModifier) => {
    if (!this.product) return;

    const newItems = this.product.items?.map((_item) => {
      if (productUtils.isSameComboItems(_item, item)) {
        return {
          ..._item,
          modifiers: _item.modifiers?.map((_modifier) => (_modifier.id === modifier.id ? modifier : _modifier)),
        };
      }

      return _item;
    });

    this.product = { ...this.product, items: newItems };

    if (this.modifiersWithError.length) this.validateModifiers();
  };

  @action public onChangeExclusions = (id: number) => {
    if (!this.product) return;

    const newExclusions = this.product.exclusions.map((exclusion) =>
      exclusion.id === id ? { ...exclusion, selected: !exclusion.selected } : exclusion
    );

    this.product = { ...this.product, exclusions: newExclusions };
  };
  @action public onChangeExclusionsInComboItem = (item: IResponseComboItem, id: number) => {
    if (!this.product) return;

    const newItems = this.product.items?.map((_item) => {
      if (productUtils.isSameComboItems(_item, item)) {
        return {
          ..._item,
          exclusions: _item.exclusions?.map((exclusion) => (id === exclusion.id ? { ...exclusion, selected: !exclusion.selected } : exclusion)),
        };
      }

      return _item;
    });

    this.product = { ...this.product, items: newItems };
  };

  @action public onChangeNotes = (notes: string) => {
    this.product = this.product ? { ...this.product, notes } : undefined;
  };

  @action public onSelectMealItem = (item: IResponseComboItem) => {
    const selectItemHandler = (_item: IResponseComboItem): IResponseComboItem => {
      if (productUtils.isSameComboItems(_item, item)) return { ..._item, is_selected: 1 };
      if (productUtils.isSameComboItemsSizeGroup(_item, item)) return { ..._item, is_selected: 0 };
      return _item;
    };

    const newItems: IResponseComboItem[] = this.product?.items?.map((_item) => selectItemHandler(_item)) || [];
    if (!newItems.length) return;

    this.product = this.product ? { ...this.product, items: newItems } : undefined;
  };

  public onSubmit = () => {
    if (!this.product || this.productError) return;

    this.validateModifiers();

    console.log(this.modifiersWithError, !appStore.settings?.is_restaurant_working);
    if (this.modifiersWithError.length) {
      this.setNeedScrollToError(true);
      return;
    }

    // Restaurant is closed
    if (!appStore.settings?.is_restaurant_working) {
      this.setNeedScrollToError(true);
      showProductModalErrorMessage({ title: 'txt_closed', message: 'the_restaurants_is_closed_now' });
      return;
    }

    this.submit();
  };
  private submit = () => {
    if (this.product) modalStore.orderProduct?.onSubmit(this.product);
    modalStore.closeOrderProduct();
  };
}

export const orderProductModalStore = new OrderProductModalStore();
